import { NativeVideoEffects } from '@/components/video-effects/NativeVideoEffects';
import { useHasVideoEffects } from '@/components/video-effects/VideoEffectsContext';
import { withStillCorsCacheKey } from '@/lib/cover/withStillCorsCacheKey';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { decidePreviewVideoSync } from '@/lib/preview/decidePreviewVideoSync';

/** One HTML5 element for the editor Player. It plays through a scene and
 *  seeks only when the composition jumps to a different source range, so
 *  each cut does not open a new media load. */
export const EditorPlaybackVideo: React.FC<{
  videoUrl: string;
  sourceTime: number;
  playing: boolean;
  volume: number;
  muted?: boolean;
  objectFit?: 'contain' | 'cover' | 'fill';
  style?: React.CSSProperties;
  /** The audible element is the only one allowed to hold the player clock. */
  reportBuffering?: boolean;
  onBufferingChange?: (buffering: boolean) => void;
  onReady?: () => void;
  onMediaError?: (error: Error) => void;
}> = ({
  videoUrl,
  sourceTime,
  playing,
  volume,
  muted = false,
  objectFit = 'contain',
  style,
  reportBuffering = true,
  onBufferingChange,
  onReady,
  onMediaError,
}) => {
  const ref = useRef<HTMLVideoElement>(null);
  const effectsActive = useHasVideoEffects();
  const mediaUrl = effectsActive ? withStillCorsCacheKey(videoUrl) : videoUrl;
  const previousSourceTime = useRef<number | null>(null);
  const pendingSeek = useRef<number | null>(null);
  const holding = useRef(false);
  const readySent = useRef(false);
  // The Player's clock stays on its frame for a while after play(): Remotion
  // waits for the AudioContext to resume, and the editor re-renders first.
  // Starting the element before the frame moves lets it run ahead; the drift
  // check then seeks it back, that seek fires `waiting`, and the buffering
  // hold pauses the Player again — an endless play/pause loop. So after any
  // (re)start the element waits until the source time leaves this anchor.
  const clockAnchor = useRef<number | null>(null);
  const wasPlaying = useRef(false);
  const playingRef = useRef(playing);
  playingRef.current = playing;
  const onBufferingChangeRef = useRef(onBufferingChange);
  onBufferingChangeRef.current = onBufferingChange;
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const onMediaErrorRef = useRef(onMediaError);
  onMediaErrorRef.current = onMediaError;

  useEffect(() => {
    previousSourceTime.current = null;
    pendingSeek.current = null;
    holding.current = false;
    readySent.current = false;
    clockAnchor.current = null;
  }, [mediaUrl]);

  useLayoutEffect(() => {
    const video = ref.current;
    if (!video) return;
    if (playing && !wasPlaying.current) clockAnchor.current = sourceTime;
    if (!playing) clockAnchor.current = null;
    wasPlaying.current = playing;
    if (holding.current) {
      previousSourceTime.current = sourceTime;
      return;
    }
    const action = decidePreviewVideoSync({
      sourceTime,
      previousSourceTime: previousSourceTime.current,
      mediaTime: video.currentTime,
      playing,
      pendingSeek: pendingSeek.current,
    });
    previousSourceTime.current = sourceTime;
    if (action.type === 'seek') {
      pendingSeek.current = action.sourceTime;
      video.currentTime = action.sourceTime;
    } else if (pendingSeek.current !== null && Math.abs(video.currentTime - sourceTime) <= 0.12) {
      pendingSeek.current = null;
    }
    const audible = Math.max(0, Math.min(1, volume));
    if (Math.abs(video.volume - audible) > 0.01) video.volume = audible;
    const clockMoving = clockAnchor.current === null || sourceTime !== clockAnchor.current;
    if (playing && clockMoving) {
      clockAnchor.current = null;
      if (video.paused && typeof video.play === 'function') {
        const played = video.play();
        if (played && typeof played.catch === 'function') played.catch(() => {});
      }
    } else if (!video.paused) {
      video.pause();
    }
  }, [sourceTime, playing, volume, mediaUrl]);

  return (
    <>
    <video
      ref={ref}
      data-testid="editor-playback-video"
      data-source-time={sourceTime}
      crossOrigin={effectsActive ? "anonymous" : undefined}
      src={mediaUrl}
      muted={muted}
      playsInline
      preload="auto"
      style={{ width: '100%', height: '100%', objectFit, ...style }}
      onLoadedMetadata={() => { if (ref.current) ref.current.currentTime = sourceTime; }}
      onWaiting={() => {
        if (!reportBuffering || !playingRef.current) return;
        holding.current = true;
        // Freeze with the Player. Left running, the element resumes by itself
        // on `canplay`, ahead of a Player that is still restarting.
        ref.current?.pause();
        onBufferingChangeRef.current?.(true);
      }}
      onCanPlay={() => {
        if (!readySent.current) {
          readySent.current = true;
          onReadyRef.current?.();
        }
        if (!holding.current) return;
        holding.current = false;
        // Resume through the Player: the layout effect restarts the element
        // once the Player's frame actually moves off this one.
        clockAnchor.current = previousSourceTime.current;
        onBufferingChangeRef.current?.(false);
      }}
      onError={() => onMediaErrorRef.current?.(new Error('media failed'))}
    />
    <NativeVideoEffects videoRef={ref} sourceKey={mediaUrl} style={{ objectFit, ...style }} />
    </>
  );
};
